import { __decorate } from 'tslib';
import { DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, EventEmitter, ViewChild, Injector, Injectable, inject, booleanAttribute, Directive, NgModule } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil, filter, take, switchMap } from 'rxjs/operators';
import * as i1 from 'ng-zorro-antd/core/config';
import { WithConfig } from 'ng-zorro-antd/core/config';
import { CdkDragHandle, CdkDrag } from '@angular/cdk/drag-drop';
import { fadeMotion } from 'ng-zorro-antd/core/animation';
import * as i3 from 'ng-zorro-antd/core/services';
import { NzDestroyService } from 'ng-zorro-antd/core/services';
import { isNotNil } from 'ng-zorro-antd/core/util';
import * as i5 from 'ng-zorro-antd/icon';
import { NzIconModule } from 'ng-zorro-antd/icon';
import * as i4 from '@angular/platform-browser';
import * as i1$1 from '@angular/cdk/overlay';
import { OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ESCAPE, LEFT_ARROW, RIGHT_ARROW, hasModifierKey } from '@angular/cdk/keycodes';
import * as i3$1 from '@angular/cdk/bidi';

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
const _c0 = ["*"];
const _c1 = ["imgRef"];
const _c2 = ["imagePreviewWrapper"];
function NzImagePreviewComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 13);
    i0.ɵɵlistener("click", function NzImagePreviewComponent_Conditional_2_Template_div_click_0_listener($event) {
      i0.ɵɵrestoreView(_r2);
      const ctx_r2 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r2.onSwitchLeft($event));
    });
    i0.ɵɵelement(1, "span", 14);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(2, "div", 15);
    i0.ɵɵlistener("click", function NzImagePreviewComponent_Conditional_2_Template_div_click_2_listener($event) {
      i0.ɵɵrestoreView(_r2);
      const ctx_r2 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r2.onSwitchRight($event));
    });
    i0.ɵɵelement(3, "span", 16);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("ant-image-preview-switch-left-disabled", ctx_r2.index <= 0);
    i0.ɵɵadvance(2);
    i0.ɵɵclassProp("ant-image-preview-switch-right-disabled", ctx_r2.index >= ctx_r2.images.length - 1);
  }
}
function NzImagePreviewComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 5);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate2("", ctx_r2.index + 1, " / ", ctx_r2.images.length, "");
  }
}
function NzImagePreviewComponent_For_6_Template(rf, ctx) {
  if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "li", 17);
    i0.ɵɵlistener("click", function NzImagePreviewComponent_For_6_Template_li_click_0_listener() {
      const option_r5 = i0.ɵɵrestoreView(_r4).$implicit;
      return i0.ɵɵresetView(option_r5.onClick());
    });
    i0.ɵɵelement(1, "span", 18);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    let tmp_13_0;
    const option_r5 = ctx.$implicit;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("ant-image-preview-operations-operation-disabled", ctx_r2.zoomOutDisabled && option_r5.type === "zoomOut");
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzType", option_r5.icon)("nzRotate", (tmp_13_0 = option_r5.rotate) !== null && tmp_13_0 !== undefined ? tmp_13_0 : 0);
  }
}
function NzImagePreviewComponent_For_15_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "img", 20, 1);
  }
  if (rf & 2) {
    const image_r6 = i0.ɵɵnextContext().$implicit;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("width", image_r6.width)("height", image_r6.height)("transform", ctx_r2.previewImageTransform);
    i0.ɵɵattribute("src", ctx_r2.sanitizerResourceUrl(image_r6.src), i0.ɵɵsanitizeUrl)("srcset", image_r6.srcset)("alt", image_r6.alt);
  }
}
function NzImagePreviewComponent_For_15_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzImagePreviewComponent_For_15_Conditional_0_Template, 2, 9, "img", 19);
  }
  if (rf & 2) {
    const ɵ$index_37_r7 = ctx.$index;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵconditional(ɵ$index_37_r7 === ctx_r2.index ? 0 : -1);
  }
}
let NzImageGroupComponent = /*#__PURE__*/(() => {
  class NzImageGroupComponent {
    constructor() {
      this.nzScaleStep = null;
      this.images = [];
    }
    addImage(image) {
      this.images.push(image);
    }
    static {
      this.ɵfac = function NzImageGroupComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzImageGroupComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzImageGroupComponent,
        selectors: [["nz-image-group"]],
        inputs: {
          nzScaleStep: "nzScaleStep"
        },
        exportAs: ["nzImageGroup"],
        standalone: true,
        features: [i0.ɵɵStandaloneFeature],
        ngContentSelectors: _c0,
        decls: 1,
        vars: 0,
        template: function NzImageGroupComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
          }
        },
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzImageGroupComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
/** @deprecated Will be removed in v19. */
const FADE_CLASS_NAME_MAP = {
  enter: 'ant-fade-enter',
  enterActive: 'ant-fade-enter-active',
  leave: 'ant-fade-leave',
  leaveActive: 'ant-fade-leave-active'
};
/** @deprecated Will be removed in v19. */
const IMAGE_PREVIEW_MASK_CLASS_NAME = 'ant-image-preview-mask';
const NZ_CONFIG_MODULE_NAME$1 = 'image';

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
/**
 * fit content details: https://github.com/NG-ZORRO/ng-zorro-antd/pull/6154#issuecomment-745025554
 *
 * calc position x,y point
 *
 * CASE (width <= clientWidth && height <= clientHeight):
 *
 * ------------- clientWidth -------------
 * |                                     |
 * |        ------ width ------          |
 * |        |                 |          |
 * |        |                 |          |
 * client   height            |          |
 * Height   |                 |          |
 * |        |                 |          |
 * |        -------------------          |
 * |                                     |
 * |                                     |
 * ---------------------------------------
 * fixedPosition = { x: 0, y: 0 }
 *
 *
 *
 * CASE (width > clientWidth || height > clientHeight):
 *
 * ------------- clientWidth -------------
 * |        |                            |
 * |        top                          |
 * |        |                            |
 * |--left--|--------------- width -----------------
 * |        |                                      |
 * client   |                                      |
 * Height   |                                      |
 * |        |                                      |
 * |        |                                      |
 * |        height                                 |
 * |        |                                      |
 * ---------|                                      |
 *          |                                      |
 *          |                                      |
 *          |                                      |
 *          ----------------------------------------
 *
 *
 * - left || top > 0
 *   left -> 0 || top -> 0
 *
 * - (left + width) < clientWidth || (top + height) < clientHeight
 * - left | top + width | height < clientWidth | clientHeight -> Back left | top + width | height === clientWidth | clientHeight
 *
 * DEFAULT:
 * - hold position
 *
 */
function getFitContentPosition(params) {
  let fixPos = {};
  if (params.width <= params.clientWidth && params.height <= params.clientHeight) {
    fixPos = {
      x: 0,
      y: 0
    };
  }
  if (params.width > params.clientWidth || params.height > params.clientHeight) {
    fixPos = {
      x: fitPoint(params.left, params.width, params.clientWidth),
      y: fitPoint(params.top, params.height, params.clientHeight)
    };
  }
  return fixPos;
}
function getOffset(node) {
  const box = node.getBoundingClientRect();
  const docElem = document.documentElement;
  // use docElem.scrollLeft to support IE
  return {
    left: box.left + (window.pageXOffset || docElem.scrollLeft) - (docElem.clientLeft || document.body.clientLeft || 0),
    top: box.top + (window.pageYOffset || docElem.scrollTop) - (docElem.clientTop || document.body.clientTop || 0)
  };
}
function getClientSize() {
  const width = document.documentElement.clientWidth;
  const height = window.innerHeight || document.documentElement.clientHeight;
  return {
    width,
    height
  };
}
function fitPoint(start, size, clientSize) {
  const startAddSize = start + size;
  const offsetStart = (size - clientSize) / 2;
  let distance = null;
  if (size > clientSize) {
    if (start > 0) {
      distance = offsetStart;
    }
    if (start < 0 && startAddSize < clientSize) {
      distance = -offsetStart;
    }
  } else {
    if (start < 0 || startAddSize > clientSize) {
      distance = start < 0 ? offsetStart : -offsetStart;
    }
  }
  return distance;
}

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
class NzImagePreviewOptions {
  constructor() {
    this.nzKeyboard = true;
    this.nzNoAnimation = false;
    this.nzMaskClosable = true;
    this.nzCloseOnNavigation = true;
  }
}
const initialPosition = {
  x: 0,
  y: 0
};
const NZ_DEFAULT_SCALE_STEP = 0.5;
const NZ_DEFAULT_ZOOM = 1;
const NZ_DEFAULT_ROTATE = 0;
let NzImagePreviewComponent = /*#__PURE__*/(() => {
  class NzImagePreviewComponent {
    get animationDisabled() {
      return this.config.nzNoAnimation ?? false;
    }
    get maskClosable() {
      const defaultConfig = this.nzConfigService.getConfigForComponent(NZ_CONFIG_MODULE_NAME$1) || {};
      return this.config.nzMaskClosable ?? defaultConfig.nzMaskClosable ?? true;
    }
    constructor(ngZone, cdr, nzConfigService, config, destroy$, sanitizer) {
      this.ngZone = ngZone;
      this.cdr = cdr;
      this.nzConfigService = nzConfigService;
      this.config = config;
      this.destroy$ = destroy$;
      this.sanitizer = sanitizer;
      this._defaultNzZoom = NZ_DEFAULT_ZOOM;
      this._defaultNzScaleStep = NZ_DEFAULT_SCALE_STEP;
      this._defaultNzRotate = NZ_DEFAULT_ROTATE;
      this.images = [];
      this.index = 0;
      this.isDragging = false;
      this.visible = true;
      this.animationStateChanged = new EventEmitter();
      this.scaleStepMap = new Map();
      this.previewImageTransform = '';
      this.previewImageWrapperTransform = '';
      this.operations = [{
        icon: 'close',
        onClick: () => {
          this.onClose();
        },
        type: 'close'
      }, {
        icon: 'zoom-in',
        onClick: () => {
          this.onZoomIn();
        },
        type: 'zoomIn'
      }, {
        icon: 'zoom-out',
        onClick: () => {
          this.onZoomOut();
        },
        type: 'zoomOut'
      }, {
        icon: 'rotate-right',
        onClick: () => {
          this.onRotateRight();
        },
        type: 'rotateRight'
      }, {
        icon: 'rotate-left',
        onClick: () => {
          this.onRotateLeft();
        },
        type: 'rotateLeft'
      }, {
        icon: 'swap',
        onClick: () => {
          this.onHorizontalFlip();
        },
        type: 'flipHorizontally'
      }, {
        icon: 'swap',
        onClick: () => {
          this.onVerticalFlip();
        },
        type: 'flipVertically',
        rotate: 90
      }];
      this.zoomOutDisabled = false;
      this.position = {
        ...initialPosition
      };
      this.closeClick = new EventEmitter();
      this.zoom = this.config.nzZoom ?? this._defaultNzZoom;
      this.scaleStep = this.config.nzScaleStep ?? this._defaultNzScaleStep;
      this.rotate = this.config.nzRotate ?? this._defaultNzRotate;
      this.flipHorizontally = this.config.nzFlipHorizontally ?? false;
      this.flipVertically = this.config.nzFlipVertically ?? false;
      this.updateZoomOutDisabled();
      this.updatePreviewImageTransform();
      this.updatePreviewImageWrapperTransform();
    }
    ngOnInit() {
      this.ngZone.runOutsideAngular(() => {
        fromEvent(this.imagePreviewWrapper.nativeElement, 'mousedown').pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.isDragging = true;
        });
        fromEvent(this.imagePreviewWrapper.nativeElement, 'wheel').pipe(takeUntil(this.destroy$)).subscribe(event => {
          this.ngZone.run(() => this.wheelZoomEventHandler(event));
        });
      });
    }
    setImages(images, scaleStepMap) {
      if (scaleStepMap) this.scaleStepMap = scaleStepMap;
      this.images = images;
      this.markForCheck();
    }
    switchTo(index) {
      this.index = index;
      this.markForCheck();
    }
    next() {
      if (this.index < this.images.length - 1) {
        this.reset();
        this.index++;
        this.updatePreviewImageTransform();
        this.updatePreviewImageWrapperTransform();
        this.updateZoomOutDisabled();
        this.markForCheck();
      }
    }
    prev() {
      if (this.index > 0) {
        this.reset();
        this.index--;
        this.updatePreviewImageTransform();
        this.updatePreviewImageWrapperTransform();
        this.updateZoomOutDisabled();
        this.markForCheck();
      }
    }
    markForCheck() {
      this.cdr.markForCheck();
    }
    onClose() {
      this.visible = false;
      this.closeClick.emit();
    }
    onZoomIn() {
      const zoomStep = this.scaleStepMap.get(this.images[this.index].src ?? this.images[this.index].srcset) ?? this.scaleStep;
      this.zoom += zoomStep;
      this.updatePreviewImageTransform();
      this.updateZoomOutDisabled();
    }
    onZoomOut() {
      if (this.zoom > 1) {
        const zoomStep = this.scaleStepMap.get(this.images[this.index].src ?? this.images[this.index].srcset) ?? this.scaleStep;
        this.zoom -= zoomStep;
        this.updatePreviewImageTransform();
        this.updateZoomOutDisabled();
        if (this.zoom <= 1) {
          this.reCenterImage();
        }
      }
    }
    onRotateRight() {
      this.rotate += 90;
      this.updatePreviewImageTransform();
    }
    onRotateLeft() {
      this.rotate -= 90;
      this.updatePreviewImageTransform();
    }
    onSwitchLeft(event) {
      event.preventDefault();
      event.stopPropagation();
      this.prev();
    }
    onSwitchRight(event) {
      event.preventDefault();
      event.stopPropagation();
      this.next();
    }
    onHorizontalFlip() {
      this.flipHorizontally = !this.flipHorizontally;
      this.updatePreviewImageTransform();
    }
    onVerticalFlip() {
      this.flipVertically = !this.flipVertically;
      this.updatePreviewImageTransform();
    }
    wheelZoomEventHandler(event) {
      event.preventDefault();
      event.stopPropagation();
      this.handlerImageTransformationWhileZoomingWithMouse(event, event.deltaY);
      this.handleImageScaleWhileZoomingWithMouse(event.deltaY);
      this.updatePreviewImageWrapperTransform();
      this.updatePreviewImageTransform();
      this.markForCheck();
    }
    onAnimationStart(event) {
      this.animationStateChanged.emit(event);
    }
    onAnimationDone(event) {
      this.animationStateChanged.emit(event);
    }
    onDragEnd(event) {
      this.isDragging = false;
      const width = this.imageRef.nativeElement.offsetWidth * this.zoom;
      const height = this.imageRef.nativeElement.offsetHeight * this.zoom;
      const {
        left,
        top
      } = getOffset(this.imageRef.nativeElement);
      const {
        width: clientWidth,
        height: clientHeight
      } = getClientSize();
      const isRotate = this.rotate % 180 !== 0;
      const fitContentParams = {
        width: isRotate ? height : width,
        height: isRotate ? width : height,
        left,
        top,
        clientWidth,
        clientHeight
      };
      const fitContentPos = getFitContentPosition(fitContentParams);
      if (isNotNil(fitContentPos.x) || isNotNil(fitContentPos.y)) {
        this.position = {
          ...this.position,
          ...fitContentPos
        };
      } else if (!isNotNil(fitContentPos.x) && !isNotNil(fitContentPos.y)) {
        this.position = {
          x: event.source.getFreeDragPosition().x,
          y: event.source.getFreeDragPosition().y
        };
      }
    }
    sanitizerResourceUrl(url) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    updatePreviewImageTransform() {
      this.previewImageTransform = `scale3d(${this.zoom * (this.flipHorizontally ? -1 : 1)}, ${this.zoom * (this.flipVertically ? -1 : 1)}, 1) rotate(${this.rotate}deg)`;
    }
    updatePreviewImageWrapperTransform() {
      this.previewImageWrapperTransform = `translate3d(${this.position.x}px, ${this.position.y}px, 0)`;
    }
    updateZoomOutDisabled() {
      this.zoomOutDisabled = this.zoom <= 1;
    }
    handlerImageTransformationWhileZoomingWithMouse(event, deltaY) {
      let scaleValue;
      const imageElement = this.imageRef.nativeElement;
      const elementTransform = getComputedStyle(imageElement).transform;
      const matrixValue = elementTransform.match(/matrix.*\((.+)\)/);
      if (matrixValue) {
        scaleValue = +matrixValue[1].split(', ')[0];
      } else {
        scaleValue = this.zoom;
      }
      const x = (event.clientX - imageElement.getBoundingClientRect().x) / scaleValue;
      const y = (event.clientY - imageElement.getBoundingClientRect().y) / scaleValue;
      const halfOfScaleStepValue = deltaY < 0 ? this.scaleStep / 2 : -this.scaleStep / 2;
      this.position.x += -x * halfOfScaleStepValue * 2 + imageElement.offsetWidth * halfOfScaleStepValue;
      this.position.y += -y * halfOfScaleStepValue * 2 + imageElement.offsetHeight * halfOfScaleStepValue;
    }
    handleImageScaleWhileZoomingWithMouse(deltaY) {
      if (this.isZoomedInWithMouseWheel(deltaY)) {
        this.onZoomIn();
      } else {
        this.onZoomOut();
      }
      if (this.zoom <= 1) {
        this.reCenterImage();
      }
    }
    isZoomedInWithMouseWheel(delta) {
      return delta < 0;
    }
    reset() {
      this.zoom = this.config.nzZoom ?? this._defaultNzZoom;
      this.scaleStep = this.config.nzScaleStep ?? this._defaultNzScaleStep;
      this.rotate = this.config.nzRotate ?? this._defaultNzRotate;
      this.flipHorizontally = false;
      this.flipVertically = false;
      this.reCenterImage();
    }
    reCenterImage() {
      this.position = {
        ...initialPosition
      };
    }
    static {
      this.ɵfac = function NzImagePreviewComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzImagePreviewComponent)(i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.NzConfigService), i0.ɵɵdirectiveInject(NzImagePreviewOptions), i0.ɵɵdirectiveInject(i3.NzDestroyService), i0.ɵɵdirectiveInject(i4.DomSanitizer));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzImagePreviewComponent,
        selectors: [["nz-image-preview"]],
        viewQuery: function NzImagePreviewComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c1, 5);
            i0.ɵɵviewQuery(_c2, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.imageRef = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.imagePreviewWrapper = _t.first);
          }
        },
        hostAttrs: [1, "ant-image-preview-root"],
        hostVars: 6,
        hostBindings: function NzImagePreviewComponent_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵsyntheticHostListener("@fadeMotion.start", function NzImagePreviewComponent_animation_fadeMotion_start_HostBindingHandler($event) {
              return ctx.onAnimationStart($event);
            })("@fadeMotion.done", function NzImagePreviewComponent_animation_fadeMotion_done_HostBindingHandler($event) {
              return ctx.onAnimationDone($event);
            });
          }
          if (rf & 2) {
            i0.ɵɵsyntheticHostProperty("@.disabled", ctx.config.nzNoAnimation)("@fadeMotion", ctx.visible ? "enter" : "leave");
            i0.ɵɵstyleProp("z-index", ctx.config.nzZIndex);
            i0.ɵɵclassProp("ant-image-preview-moving", ctx.isDragging);
          }
        },
        exportAs: ["nzImagePreview"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([NzDestroyService]), i0.ɵɵStandaloneFeature],
        decls: 17,
        vars: 5,
        consts: [["imagePreviewWrapper", ""], ["imgRef", ""], [1, "ant-image-preview-mask"], [1, "ant-image-preview-operations-wrapper"], [1, "ant-image-preview-operations"], [1, "ant-image-preview-operations-progress"], [1, "ant-image-preview-operations-operation", 3, "ant-image-preview-operations-operation-disabled"], ["tabindex", "-1", 1, "ant-image-preview-wrap", 3, "click"], ["role", "dialog", "aria-modal", "true", 1, "ant-image-preview"], ["tabindex", "0", "aria-hidden", "true", 2, "width", "0", "height", "0", "overflow", "hidden", "outline", "none"], [1, "ant-image-preview-content"], [1, "ant-image-preview-body"], ["cdkDrag", "", 1, "ant-image-preview-img-wrapper", 3, "cdkDragEnded", "cdkDragFreeDragPosition"], [1, "ant-image-preview-switch-left", 3, "click"], ["nz-icon", "", "nzType", "left", "nzTheme", "outline"], [1, "ant-image-preview-switch-right", 3, "click"], ["nz-icon", "", "nzType", "right", "nzTheme", "outline"], [1, "ant-image-preview-operations-operation", 3, "click"], ["nz-icon", "", "nzTheme", "outline", 1, "ant-image-preview-operations-icon", 3, "nzType", "nzRotate"], ["cdkDragHandle", "", 1, "ant-image-preview-img", 3, "width", "height", "transform"], ["cdkDragHandle", "", 1, "ant-image-preview-img"]],
        template: function NzImagePreviewComponent_Template(rf, ctx) {
          if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵelement(0, "div", 2);
            i0.ɵɵelementStart(1, "div", 3);
            i0.ɵɵtemplate(2, NzImagePreviewComponent_Conditional_2_Template, 4, 4);
            i0.ɵɵelementStart(3, "ul", 4);
            i0.ɵɵtemplate(4, NzImagePreviewComponent_Conditional_4_Template, 2, 2, "li", 5);
            i0.ɵɵrepeaterCreate(5, NzImagePreviewComponent_For_6_Template, 2, 4, "li", 6, i0.ɵɵrepeaterTrackByIdentity);
            i0.ɵɵelementEnd()();
            i0.ɵɵelementStart(7, "div", 7);
            i0.ɵɵlistener("click", function NzImagePreviewComponent_Template_div_click_7_listener($event) {
              i0.ɵɵrestoreView(_r1);
              return i0.ɵɵresetView(ctx.maskClosable && $event.target === $event.currentTarget && ctx.onClose());
            });
            i0.ɵɵelementStart(8, "div", 8);
            i0.ɵɵelement(9, "div", 9);
            i0.ɵɵelementStart(10, "div", 10)(11, "div", 11)(12, "div", 12, 0);
            i0.ɵɵlistener("cdkDragEnded", function NzImagePreviewComponent_Template_div_cdkDragEnded_12_listener($event) {
              i0.ɵɵrestoreView(_r1);
              return i0.ɵɵresetView(ctx.onDragEnd($event));
            });
            i0.ɵɵrepeaterCreate(14, NzImagePreviewComponent_For_15_Template, 1, 1, null, null, i0.ɵɵrepeaterTrackByIdentity);
            i0.ɵɵelementEnd()()();
            i0.ɵɵelement(16, "div", 9);
            i0.ɵɵelementEnd()();
          }
          if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵconditional(ctx.images.length > 1 ? 2 : -1);
            i0.ɵɵadvance(2);
            i0.ɵɵconditional(ctx.images.length > 1 ? 4 : -1);
            i0.ɵɵadvance();
            i0.ɵɵrepeater(ctx.operations);
            i0.ɵɵadvance(7);
            i0.ɵɵstyleProp("transform", ctx.previewImageWrapperTransform);
            i0.ɵɵproperty("cdkDragFreeDragPosition", ctx.position);
            i0.ɵɵadvance(2);
            i0.ɵɵrepeater(ctx.images);
          }
        },
        dependencies: [NzIconModule, i5.NzIconDirective, CdkDragHandle, CdkDrag],
        encapsulation: 2,
        data: {
          animation: [fadeMotion]
        },
        changeDetection: 0
      });
    }
  }
  return NzImagePreviewComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
class NzImagePreviewRef {
  constructor(previewInstance, config, overlayRef) {
    this.previewInstance = previewInstance;
    this.config = config;
    this.overlayRef = overlayRef;
    this.destroy$ = new Subject();
    overlayRef.keydownEvents().pipe(filter(event => this.config.nzKeyboard && (event.keyCode === ESCAPE || event.keyCode === LEFT_ARROW || event.keyCode === RIGHT_ARROW) && !hasModifierKey(event))).subscribe(event => {
      event.preventDefault();
      if (event.keyCode === ESCAPE) {
        previewInstance.onClose();
      }
      if (event.keyCode === LEFT_ARROW) {
        this.prev();
      }
      if (event.keyCode === RIGHT_ARROW) {
        this.next();
      }
    });
    overlayRef.detachments().subscribe(() => {
      this.overlayRef.dispose();
    });
    previewInstance.closeClick.pipe(take(1), switchMap(() => previewInstance.animationStateChanged), filter(event => event.phaseName === 'done'), takeUntil(this.destroy$)).subscribe(() => {
      this.close();
    });
  }
  switchTo(index) {
    this.previewInstance.switchTo(index);
  }
  next() {
    this.previewInstance.next();
  }
  prev() {
    this.previewInstance.prev();
  }
  close() {
    this.destroy$.next();
    this.overlayRef.dispose();
  }
}
let NzImageService = /*#__PURE__*/(() => {
  class NzImageService {
    constructor(overlay, injector, nzConfigService, directionality) {
      this.overlay = overlay;
      this.injector = injector;
      this.nzConfigService = nzConfigService;
      this.directionality = directionality;
    }
    preview(images, options, zoomMap) {
      return this.display(images, options, zoomMap);
    }
    display(images, config, scaleStepMap) {
      const configMerged = {
        ...new NzImagePreviewOptions(),
        ...(config ?? {})
      };
      const overlayRef = this.createOverlay(configMerged);
      const previewComponent = this.attachPreviewComponent(overlayRef, configMerged);
      previewComponent.setImages(images, scaleStepMap);
      const previewRef = new NzImagePreviewRef(previewComponent, configMerged, overlayRef);
      previewComponent.previewRef = previewRef;
      return previewRef;
    }
    attachPreviewComponent(overlayRef, config) {
      const injector = Injector.create({
        parent: this.injector,
        providers: [{
          provide: OverlayRef,
          useValue: overlayRef
        }, {
          provide: NzImagePreviewOptions,
          useValue: config
        }]
      });
      const containerPortal = new ComponentPortal(NzImagePreviewComponent, null, injector);
      const containerRef = overlayRef.attach(containerPortal);
      return containerRef.instance;
    }
    createOverlay(config) {
      const globalConfig = this.nzConfigService.getConfigForComponent(NZ_CONFIG_MODULE_NAME$1) || {};
      const overLayConfig = new OverlayConfig({
        scrollStrategy: this.overlay.scrollStrategies.block(),
        positionStrategy: this.overlay.position().global(),
        disposeOnNavigation: config.nzCloseOnNavigation ?? globalConfig.nzCloseOnNavigation ?? true,
        direction: config.nzDirection || globalConfig.nzDirection || this.directionality.value
      });
      return this.overlay.create(overLayConfig);
    }
    static {
      this.ɵfac = function NzImageService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzImageService)(i0.ɵɵinject(i1$1.Overlay), i0.ɵɵinject(i0.Injector), i0.ɵɵinject(i1.NzConfigService), i0.ɵɵinject(i3$1.Directionality));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NzImageService,
        factory: NzImageService.ɵfac
      });
    }
  }
  return NzImageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const NZ_CONFIG_MODULE_NAME = 'image';
let NzImageDirective = /*#__PURE__*/(() => {
  class NzImageDirective {
    get previewable() {
      return !this.nzDisablePreview && this.status !== 'error';
    }
    constructor(nzConfigService, elementRef, nzImageService, cdr, directionality) {
      this.nzConfigService = nzConfigService;
      this.elementRef = elementRef;
      this.nzImageService = nzImageService;
      this.cdr = cdr;
      this.directionality = directionality;
      this._nzModuleName = NZ_CONFIG_MODULE_NAME;
      this.nzSrc = '';
      this.nzSrcset = '';
      this.nzDisablePreview = false;
      this.nzFallback = null;
      this.nzPlaceholder = null;
      this.nzScaleStep = null;
      this.status = 'normal';
      this.backLoadDestroy$ = new Subject();
      this.destroy$ = new Subject();
      this.document = inject(DOCUMENT);
      this.parentGroup = inject(NzImageGroupComponent, {
        optional: true
      });
    }
    ngOnInit() {
      this.backLoad();
      if (this.parentGroup) {
        this.parentGroup.addImage(this);
      }
      if (this.directionality) {
        this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe(direction => {
          this.dir = direction;
          this.cdr.detectChanges();
        });
        this.dir = this.directionality.value;
      }
    }
    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }
    onPreview() {
      if (!this.previewable) {
        return;
      }
      if (this.parentGroup) {
        // preview inside image group
        const previewAbleImages = this.parentGroup.images.filter(e => e.previewable);
        const previewImages = previewAbleImages.map(e => ({
          src: e.nzSrc,
          srcset: e.nzSrcset
        }));
        const previewIndex = previewAbleImages.findIndex(el => this === el);
        const scaleStepMap = new Map();
        previewAbleImages.forEach(imageDirective => {
          scaleStepMap.set(imageDirective.nzSrc ?? imageDirective.nzSrcset, imageDirective.nzScaleStep ?? this.parentGroup.nzScaleStep ?? this.nzScaleStep ?? NZ_DEFAULT_SCALE_STEP);
        });
        const previewRef = this.nzImageService.preview(previewImages, {
          nzDirection: this.dir
        }, scaleStepMap);
        previewRef.switchTo(previewIndex);
      } else {
        // preview not inside image group
        const previewImages = [{
          src: this.nzSrc,
          srcset: this.nzSrcset
        }];
        this.nzImageService.preview(previewImages, {
          nzDirection: this.dir,
          nzScaleStep: this.nzScaleStep ?? NZ_DEFAULT_SCALE_STEP
        });
      }
    }
    getElement() {
      return this.elementRef;
    }
    ngOnChanges(changes) {
      const {
        nzSrc
      } = changes;
      if (nzSrc) {
        this.getElement().nativeElement.src = nzSrc.currentValue;
        this.backLoad();
      }
    }
    /**
     * use internal Image object handle fallback & placeholder
     *
     * @private
     */
    backLoad() {
      this.backLoadImage = this.document.createElement('img');
      this.backLoadImage.src = this.nzSrc;
      this.backLoadImage.srcset = this.nzSrcset;
      this.status = 'loading';
      // unsubscribe last backLoad
      this.backLoadDestroy$.next();
      this.backLoadDestroy$.complete();
      this.backLoadDestroy$ = new Subject();
      if (this.backLoadImage.complete) {
        this.status = 'normal';
        this.getElement().nativeElement.src = this.nzSrc;
        this.getElement().nativeElement.srcset = this.nzSrcset;
      } else {
        if (this.nzPlaceholder) {
          this.getElement().nativeElement.src = this.nzPlaceholder;
          this.getElement().nativeElement.srcset = '';
        } else {
          this.getElement().nativeElement.src = this.nzSrc;
          this.getElement().nativeElement.srcset = this.nzSrcset;
        }
        // The `nz-image` directive can be destroyed before the `load` or `error` event is dispatched,
        // so there's no sense to keep capturing `this`.
        fromEvent(this.backLoadImage, 'load').pipe(takeUntil(this.backLoadDestroy$), takeUntil(this.destroy$)).subscribe(() => {
          this.status = 'normal';
          this.getElement().nativeElement.src = this.nzSrc;
          this.getElement().nativeElement.srcset = this.nzSrcset;
        });
        fromEvent(this.backLoadImage, 'error').pipe(takeUntil(this.backLoadDestroy$), takeUntil(this.destroy$)).subscribe(() => {
          this.status = 'error';
          if (this.nzFallback) {
            this.getElement().nativeElement.src = this.nzFallback;
            this.getElement().nativeElement.srcset = '';
          }
        });
      }
    }
    static {
      this.ɵfac = function NzImageDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzImageDirective)(i0.ɵɵdirectiveInject(i1.NzConfigService), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(NzImageService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i3$1.Directionality));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NzImageDirective,
        selectors: [["img", "nz-image", ""]],
        hostBindings: function NzImageDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function NzImageDirective_click_HostBindingHandler() {
              return ctx.onPreview();
            });
          }
        },
        inputs: {
          nzSrc: "nzSrc",
          nzSrcset: "nzSrcset",
          nzDisablePreview: [2, "nzDisablePreview", "nzDisablePreview", booleanAttribute],
          nzFallback: "nzFallback",
          nzPlaceholder: "nzPlaceholder",
          nzScaleStep: "nzScaleStep"
        },
        exportAs: ["nzImage"],
        standalone: true,
        features: [i0.ɵɵInputTransformsFeature, i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  __decorate([WithConfig()], NzImageDirective.prototype, "nzDisablePreview", void 0);
  __decorate([WithConfig()], NzImageDirective.prototype, "nzFallback", void 0);
  __decorate([WithConfig()], NzImageDirective.prototype, "nzPlaceholder", void 0);
  __decorate([WithConfig()], NzImageDirective.prototype, "nzScaleStep", void 0);
  return NzImageDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzImageModule = /*#__PURE__*/(() => {
  class NzImageModule {
    static {
      this.ɵfac = function NzImageModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzImageModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NzImageModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [NzImageService],
        imports: [NzImagePreviewComponent]
      });
    }
  }
  return NzImageModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FADE_CLASS_NAME_MAP, IMAGE_PREVIEW_MASK_CLASS_NAME, NZ_CONFIG_MODULE_NAME$1 as NZ_CONFIG_MODULE_NAME, NZ_DEFAULT_SCALE_STEP, NzImageDirective, NzImageGroupComponent, NzImageModule, NzImagePreviewComponent, NzImagePreviewOptions, NzImagePreviewRef, NzImageService, getClientSize, getFitContentPosition, getOffset };
